<template>
  <div class="wrapper">
    <InputSearchSecondary
      ref="searchInputRef"
      v-model="searchValue"
      :placeholder="t('news_page_rubrics_switcher.placeholder.search')"
    />
    <ul
      v-if="fixedRubricList.length > 0"
      class="rubrics-list scroll"
    >
      <li
        v-if="rubricsStore.rubricsOnMonitoring.length > 1"
        class="rubrics-list__item"
        :class="{'rubrics-list__item--active': isAllRubricsChosen}"
        @click="chooseAllFixedRubric(!isAllRubricsChosen)"
      >
        <Checkbox
          :model-value="isAllRubricsChosen"
          :label="t('news_page_rubrics_switcher.button.all')"
          :readonly="true"
          class="rubrics-list__item-checkbox"
          @update:model-value="val => chooseAllFixedRubric(val)"
        />
      </li>

      <li
        v-for="rubric in fixedRubricList"
        v-show="checkSearchValue(rubric.name, searchValue)"
        :key="rubric.id"
        class="rubrics-list__item"
        :class="{
          'rubrics-list__item--active': rubricsStore.chosenRubrics.includes(rubric.id),
          'rubrics-list__item--disabled': isRubricDisabledToChoose(rubric.id)}"
        @click="chooseRubric(rubric)"
      >
        <Checkbox
          :readonly="true"
          :model-value="rubricsStore.chosenRubrics.includes(rubric.id)"
          :label="rubric.name"
          :disabled="isRubricDisabledToChoose(rubric.id)"
          class="rubrics-list__item-checkbox"
        />

        <button
          v-if="LKStore.user.roleNames.includes('аналитик') || LKStore.project.isOwner"
          class="rubrics-list__menu-button"
          :class="
            {'rubrics-list__menu-button--active': isShowMenu && state.chosenItem.id === rubric.id,
             'rubrics-list__menu-button--chosen': rubricsStore.chosenRubrics.includes(rubric.id)}"
          @click.stop="openRubricMenu($event, rubric)"
        >
          <DotsIcon class="rubrics-list__item-icon" />
        </button>
      </li>
    </ul>

    <div
      v-if="notFixedRubricList.length > 0 && fixedRubricList.length > 0"
      class="hr-line"
    />

    <ul
      v-if="notFixedRubricList.length > 0"
      class="rubrics-list scroll"
    >
      <li
        v-for="rubric in notFixedRubricList"
        v-show="checkSearchValue(rubric.name, searchValue)"
        :key="rubric"
        class="rubrics-list__item rubrics-list__item--fixed"
        :class="{
          'rubrics-list__item--active': rubricsStore.chosenRubrics.includes(rubric.id),
          'rubrics-list__item--disabled': isRubricDisabledToChoose(rubric)}"
        @click="chooseRubric(rubric)"
      >
        <span class="span-question">
          <EyeCrossedIcon />
        </span>
        <span class="rubrics-list__item-title">{{ rubric.name }}</span>
        <button
          v-if="LKStore.user.roleNames.includes('аналитик') || LKStore.project.isOwner"
          class="rubrics-list__menu-button"
          :class="{'rubrics-list__menu-button--active': isShowMenu && state.chosenItem.id === rubric.id}"
          @click.stop="openRubricMenu($event, rubric)"
        >
          <DotsIcon class="rubrics-list__item-icon" />
        </button>
      </li>
    </ul>
  </div>

  <NewsPageSwitcherMenu
    v-if="isShowMenu && state.chosenItem"
    :coords="state.coords"
    type="rubrics"
    :is-fixed-rubric="state.chosenItem.is_fixed"
    @close="closeEdit"
    @edit="openEditPopup"
    @delete="openDeletePopup"
    @add-to-monitoring="addToMonitoring"
  />
</template>
<script setup>
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import {reactive, ref, defineProps, defineEmits, computed, onMounted} from "vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { t } from "@/controllerLK/AvalLocalization";
import {debounce } from "@/functions/debouncer";
import { useLK } from "@/controllerLK/LKstore";
import DotsIcon from "@/components/Icons/DotsIcon.vue";
import EyeCrossedIcon from "@/components/Icons/EyeCrossedIcon.vue";
import NewsPageSwitcherMenu from "@/components/NewsPage/NewsPageRoof/NewsPageSwitcherMenu.vue";
import { areArraysEqual } from "@/functions/basic";
import { useBlockStore } from "@/store/NewsFeed/blocks";

const props = defineProps({
  activeRubric: {
    type: [Object, null],
    default: () => {}
  }
});

const emit = defineEmits(["open-edit-popup", "open-delete-popup", "set-active-rubric"]);

const newsFeedStore = useNewsFeedStore();
const blockStore = useBlockStore();
const rubricsStore = useRubricsStore();
const LKStore = useLK();

const state = reactive({
  chosenItem: null,
  coords: {
    top: "0px",
    left: "0px"
  }
});

onMounted(() => {
  if (searchInputRef.value) {
    searchInputRef.value.focus();
  }
});

// Реф на поисковую строку
const searchInputRef = ref(null);


// Список рубрик на мониторинге
const fixedRubricList = computed(() => rubricsStore.allRubrics.filter((rubric) => rubric.is_fixed));
// Список рубрик не на мониторинге
const notFixedRubricList = computed(() => rubricsStore.allRubrics.filter((rubric) => !rubric.is_fixed));

// Значение поиска рубрики
const searchValue = ref("");

// Функция проверки наличия рубрики по поисковому запросу
const checkSearchValue = (string, substring) => {
  return string.toLowerCase().includes(substring.toLowerCase());
};

// Всплывающее меню рубрик
// Состояние меню
const isShowMenu = ref(false);

// Открытие меню
const openRubricMenu = (event, rubric) => {
  emit("set-active-rubric", rubric);
  isShowMenu.value = true;
  state.chosenItem = rubric;
  findMenuCoordinate(event);
};

// Закрытие меню
function closeEdit() {
  isShowMenu.value = false;
  state.chosenItem = null;
}

// Координаты меню для открытия.
function findMenuCoordinate(e) {
  const rect = e.target.getBoundingClientRect();
  state.coords.top = rect.top + rect.height + 5 + "px";
  state.coords.left = rect.left + "px";
}

// Рубрики
// Функция проверки запрета выбора рубрики
const isRubricDisabledToChoose = (rubricId) => {
  return allRubricsInThemes.value.length > 0 && !allRubricsInThemes.value.includes(rubricId);
};

// Список всех тем в выбранных рубриках
const allRubricsInThemes = computed(() => {
  if (blockStore.chosenThemes.length > 0) {
    let allRubricsInThemes = [];
    blockStore.allThemes.forEach((theme) => {
      if (blockStore.chosenThemes.includes(theme.id)) {
        allRubricsInThemes.push(...theme.rubric_ids);
      }
    });
    allRubricsInThemes = [...new Set(allRubricsInThemes)];
    return allRubricsInThemes;
  }
  return [];
});

// Флаг: выбраны ли все рубрики на мониторинге, в зависимости от выбранных тем.
const isAllRubricsChosen = computed(() => {
  // Если выбраны темы, то проверяем по рубрикам в темам
  if (blockStore.chosenThemes.length > 0) {
    return areArraysEqual([...rubricsStore.chosenRubrics], allRubricsInThemes.value);
  }
  // Если тема не выбрана, проверяем по всем рубрикам на мониторинге
  else {
    if (rubricsStore.chosenRubrics.length !== rubricsStore.rubricsOnMonitoring.length) {
      return false;
    }
    const sortedArray1 = [...rubricsStore.chosenRubrics].sort();
    const sortedArray2 = [...rubricsStore.rubricsOnMonitoring].sort();
    return sortedArray1.every((item, index) => item === sortedArray2[index]);
  }
});

// Функция выбора всех рубрик на мониторинге.
const chooseAllFixedRubric = (value) => {
  if (value) {
    if (allRubricsInThemes.value.length > 0) {
      rubricsStore.chosenRubrics = allRubricsInThemes.value;
    } else {
      rubricsStore.chosenRubrics = rubricsStore.rubricsOnMonitoring;
    }
  } else {
    rubricsStore.chosenRubrics = [];
  }
  updateNewsDebounced();
};

// Выбор рубрики
function chooseRubric(item) {
  if (isRubricDisabledToChoose(item.id)) return;

  if (item.is_fixed) {
    if (rubricsStore.chosenRubrics.includes(item.id)) {
      rubricsStore.chosenRubrics = rubricsStore.chosenRubrics.filter((rubric) => rubric !== item.id);
    } else {
      rubricsStore.chosenRubrics = rubricsStore.chosenRubrics.filter(id => {
        const rubric = rubricsStore.allRubrics.find(rubric => rubric.id === id);
        return rubric && rubric.is_fixed;
      });
      rubricsStore.chosenRubrics.push(item.id);
    }
  } else {
    if (rubricsStore.chosenRubrics.includes(item.id)) {
      rubricsStore.chosenRubrics = rubricsStore.chosenRubrics.filter((rubric) => rubric !== item.id);
    } else {
      rubricsStore.chosenRubrics = [item.id];
    }
  }
  newsFeedStore.searchValue = "";
  rubricsStore.chosenRubrics.length > 0 ? updateNewsDebounced() : false;
}

// Функция получения списка новостей
const updateNews = () => {
  newsFeedStore.clearNewsFeed();
  newsFeedStore.getNews();
};
const updateNewsDebounced = debounce(updateNews, 700);

// Функция постановки новости на мониторинг
const addToMonitoring = () => {
  let rubric = props.activeRubric;
  rubric.is_fixed = true;
  rubricsStore.saveChanges(rubric);

  if (!rubricsStore.chosenRubrics.length) {
    rubricsStore.chosenRubrics = [];
  }

  closeEdit();
  updateNewsDebounced();
};

// Событие открытия модального окна редактирования рубрики
function openEditPopup() {
  emit("open-edit-popup");
  closeEdit();
}

// Событие открытие модального окна удаления рубрики
function openDeletePopup() {
  emit("open-delete-popup", props.activeRubric);
  closeEdit();
}
</script>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.rubrics-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 250px;
  padding-right: 2px;

  &__menu-button {
    background-color: transparent;
    cursor: pointer;
    user-select: none;
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
    color: var(--purple-purple50);
    align-self: flex-start;

    &:hover {
      color: var(--blue-blue80);
    }

    &--chosen {
      color: var(--icon-icon-on);
    }

    &--active {
      background-color: var(--background-bg-main);
      border-radius: 50%;
    }
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    user-select: none;
    padding: 6px 14px;
    border-radius: 8px;
    gap: 5px;
    overflow-wrap: anywhere;
    position: relative;

    &--fixed {
      color: var(--text-text-pur);
    }

    &-checkbox {
      pointer-events: none;
    }

    &-title {
      flex: 1;
    }

    &:hover:not(&--disabled) {
      &:deep(input) {
        border-color: var(--icon-icon-on);
      }
    }

    &:hover:not(&--active) {
      background: var(--background-bg-hover);
    }

    &--active {
      color: var(--text-text-on);
      &:hover {
        background-color: var(--background-bg-active-hover);
      }
    }

    &--active :deep(.checkbox__label) {
      color: var(--text-text-on);
    }

    :deep(label) {
      cursor: pointer;
    }

    :deep(input) {
      align-self: flex-start;
    }
  }
}

.hr-line {
  background: #EEF1F4;
  height: 1px;
  width: 100%;
  position: relative;
}

.span-question {
  background: var(--background-bg-hover2);
  color: var(--text-text-pur);
  width: 16px;
  height: 16px;
  font-size: 12px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 16px;
  min-height: 16px;
  border-radius: 50%;
}
</style>